import {Button} from "@/components/button/Button.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {StatusPill} from "@/components/statusPill/StatusPill.tsx";
import {PillStatus} from "@/components/statusPill/StatusPill.types.ts";
import {
  ComponentStatus,
  ComponentStatusValue,
  Component as ComponentType,
  StatusPageLayoutPreset,
  StatusPageTimeFormat,
} from "@/lib/types/statuspages.ts";
import classNames from "classnames";
import {type MouseEvent, useMemo, useState} from "react";
import {FiChevronDown, FiChevronRight} from "react-icons/fi";
import {Link} from "react-router";
import styles from "./ComponentItem.module.scss";
import {ResponseTimeSparkline} from "./ResponseTimeSparkline";

export interface ComponentItemProps {
  component: ComponentType;
  componentStatusLookup: Record<string, ComponentStatus>;
  componentStatusRank: Record<string, number>;
  showResponseTime: boolean;
  showComponentDescriptions?: boolean;
  timezone?: string;
  timeFormat?: StatusPageTimeFormat;
  level?: number;
  componentLinkTo?: (serviceId: number) => string;
  layout?: StatusPageLayoutPreset;
  variant?: "default" | "inspire";
}

// For group components, average the response-time data-points of all leaf children
// so the sparkline shows an aggregated view across the group.
const aggregateGroupResponseTimeData = (subcomponents: ComponentType[]): [string | number, number][] | null => {
  const map = new Map<number, number[]>();
  for (const child of subcomponents) {
    if (child.is_group || !child.response_time_data || child.response_time_data.length === 0) continue;
    for (const [ts, val] of child.response_time_data) {
      const ms = typeof ts === "string" ? new Date(ts).getTime() : (ts as number);
      const bucket = map.get(ms);
      if (bucket) bucket.push(val);
      else map.set(ms, [val]);
    }
  }
  if (map.size === 0) return null;
  return Array.from(map.entries())
    .sort(([a], [b]) => a - b)
    .map(([ts, vals]): [number, number] => [ts, vals.reduce((s, v) => s + v, 0) / vals.length]);
};

const filterToLastHour = (data: [string | number, number][]): [string | number, number][] => {
  const cutoff = Date.now() - 60 * 60 * 1000;
  return data.filter(([ts]) => {
    const ms = typeof ts === "string" ? new Date(ts).getTime() : (ts as number);
    return ms >= cutoff;
  });
};

// Map component status values to PillStatus
const ComponentStatusPillMap: Record<ComponentStatusValue, PillStatus> = {
  [ComponentStatusValue.OPERATIONAL]: PillStatus.OPERATIONAL,
  [ComponentStatusValue.MAJOR_OUTAGE]: PillStatus.OUTAGE,
  [ComponentStatusValue.PARTIAL_OUTAGE]: PillStatus.PARTIAL_OUTAGE,
  [ComponentStatusValue.DEGRADED_PERFORMANCE]: PillStatus.DEGRADED_PERFORMANCE,
  [ComponentStatusValue.UNDER_MAINTENANCE]: PillStatus.MAINTENANCE,
  [ComponentStatusValue.NO_CHANGE]: PillStatus.NO_CHANGE,
  [ComponentStatusValue.NOTIFICATION]: PillStatus.NOTIFICATION,
};

// Plain-text status labels and CSS color classes for the minimal layout variant
const MinimalStatusDisplay: Record<ComponentStatusValue, {label: string; color: string}> = {
  [ComponentStatusValue.OPERATIONAL]: {label: "Operational", color: "green"},
  [ComponentStatusValue.MAJOR_OUTAGE]: {label: "Major Outage", color: "red"},
  [ComponentStatusValue.PARTIAL_OUTAGE]: {label: "Partial Outage", color: "orange"},
  [ComponentStatusValue.DEGRADED_PERFORMANCE]: {label: "Degraded Performance", color: "orange"},
  [ComponentStatusValue.UNDER_MAINTENANCE]: {label: "Maintenance", color: "yellow"},
  [ComponentStatusValue.NO_CHANGE]: {label: "No Change", color: "gray"},
  [ComponentStatusValue.NOTIFICATION]: {label: "Notification", color: "gray"},
};

export const ComponentItem = ({
  component,
  componentStatusLookup,
  componentStatusRank,
  showResponseTime,
  showComponentDescriptions = true,
  timezone,
  timeFormat,
  level = 0,
  componentLinkTo,
  layout = StatusPageLayoutPreset.EXPANDED,
  variant = "default",
}: ComponentItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasSubcomponents = component.subcomponents && component.subcomponents.length > 0;

  const getComponentStatus = (comp: ComponentType): string => {
    const ownStatus = comp.status || ComponentStatusValue.OPERATIONAL;

    if (comp.subcomponents && comp.subcomponents.length > 0) {
      let worstStatus = ownStatus;
      let worstRank = componentStatusRank[ownStatus] ?? 999;

      comp.subcomponents.forEach((subComp) => {
        const subStatus = getComponentStatus(subComp);
        const rank = componentStatusRank[subStatus] ?? 999;
        if (rank < worstRank) {
          worstRank = rank;
          worstStatus = subStatus;
        }
      });

      return worstStatus;
    }

    return ownStatus;
  };

  const status = getComponentStatus(component) as ComponentStatusValue;
  const pillStatus = ComponentStatusPillMap[status] || PillStatus.OPERATIONAL;

  const isGroup = !!component.is_group;
  // The drill-down page is addressed by check id, not component id, so that its URL matches the
  // legacy component page and links shared before the next-gen pages shipped keep resolving.
  const drillDownServiceId = component.has_drill_down ? (component.service_id ?? null) : null;
  const drillDownLink = componentLinkTo && drillDownServiceId != null ? componentLinkTo(drillDownServiceId) : null;

  const toggleExpanded = () => {
    if (hasSubcomponents) {
      setIsExpanded(!isExpanded);
    }
  };

  // Compute sparkline data: own data for leaf components, averaged children data for groups.
  // Filter to the last 1h to guard against stale cached points (cache TTL is 15 min,
  // so the backend window of ~67.5 min can drift beyond 1h from the current time).
  const sparklineData = useMemo(() => {
    if (!showResponseTime) return null;
    if (component.is_group) {
      const aggregated = aggregateGroupResponseTimeData((component.subcomponents || []) as ComponentType[]);
      if (!aggregated) return null;
      const filtered = filterToLastHour(aggregated);
      return filtered.length > 0 ? filtered : null;
    }
    const d = component.response_time_data;
    if (!d || d.length === 0) return null;
    const filtered = filterToLastHour(d);
    return filtered.length > 0 ? filtered : null;
  }, [component, showResponseTime]);

  // SSL/certificate components carry days-to-expiry in cached_response_time (not a response time),
  // shown as text where the sparkline would otherwise be — matching the legacy status page.
  const daysToExpiry =
    component.performance_data_type === "DAYS_TO_EXPIRY" && component.cached_response_time > 0
      ? `Expires: ${Math.round(component.cached_response_time)} days`
      : null;

  const isCompact = layout === StatusPageLayoutPreset.COMPACT && level === 0;
  const isMinimal = layout === StatusPageLayoutPreset.MINIMAL;
  // Minimal is a single-line preset for very large lists, so it stays description-free.
  const showDescription = !!component.description && showComponentDescriptions && !isMinimal;

  return (
    <div
      className={classNames(styles.componentItem, {
        [styles.componentItemExpandable]: hasSubcomponents,
        [styles.componentItemNested]: level > 0 && !isMinimal,
        [styles.open]: isExpanded && hasSubcomponents,
        [styles.compact]: isCompact,
        [styles.minimal]: isMinimal && level === 0,
        [styles.minimalNested]: isMinimal && level > 0,
      })}
    >
      <div
        className={classNames(styles.componentRow, {
          [styles.expandable]: hasSubcomponents,
          [styles.compact]: isCompact,
          [styles.minimal]: isMinimal,
        })}
        role="button"
        tabIndex={0}
        onClick={toggleExpanded}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div className={styles.componentInfo}>
          <div className={styles.componentNameRow}>
            {drillDownLink ? (
              <Button
                as={Link}
                to={drillDownLink}
                variant="linkBlue"
                size="text"
                className={classNames(styles.componentName, styles.componentNameLink)}
                // The row toggles its subcomponents on click; the name navigates instead.
                onClick={(event: MouseEvent) => event.stopPropagation()}
              >
                {component.name}
              </Button>
            ) : (
              <span
                className={classNames(styles.componentName, {
                  [styles.componentNameGroup]: isGroup || (isMinimal && hasSubcomponents),
                })}
              >
                {component.name}
              </span>
            )}
            {hasSubcomponents && <span className={styles.componentCountBadge}>{component.subcomponents!.length}</span>}
          </div>
          {showDescription && <span className={styles.componentDescription}>{component.description}</span>}
          {isCompact && (daysToExpiry || sparklineData) && (
            <div className={styles.compactSparkline}>
              {daysToExpiry ? (
                <span className={styles.expiryText}>{daysToExpiry}</span>
              ) : (
                sparklineData && (
                  <ResponseTimeSparkline data={sparklineData} timezone={timezone} timeFormat={timeFormat} />
                )
              )}
            </div>
          )}
        </div>
        {!isCompact && !isMinimal && (
          <div className={styles.sparklineCell}>
            {daysToExpiry ? (
              <span className={styles.expiryText}>{daysToExpiry}</span>
            ) : (
              sparklineData && (
                <ResponseTimeSparkline data={sparklineData} timezone={timezone} timeFormat={timeFormat} />
              )
            )}
          </div>
        )}
        <div className={styles.componentRowRight}>
          {isMinimal && component.service_uptime_sla != null && (
            <span className={styles.minimalUptime}>{component.service_uptime_sla}%</span>
          )}
          {isMinimal ? (
            <span
              className={classNames(
                styles.minimalStatusText,
                styles[`minimalStatus_${MinimalStatusDisplay[status]?.color ?? "gray"}`]
              )}
            >
              {MinimalStatusDisplay[status]?.label ?? status}
            </span>
          ) : (
            <StatusPill type="checks" status={pillStatus} variant="textAndIcon" />
          )}
          {level === 0 || hasSubcomponents ? (
            <span className={classNames(styles.componentChevron, {[styles.componentChevronDim]: !hasSubcomponents})}>
              <Icon icon={hasSubcomponents && isExpanded ? FiChevronDown : FiChevronRight} size="normal" />
            </span>
          ) : (
            isMinimal && <span className={styles.minimalChevronPlaceholder} aria-hidden="true" />
          )}
        </div>
      </div>

      {hasSubcomponents && isExpanded && (
        <div
          className={classNames(styles.componentSubcomponents, {
            [styles.compact]: isCompact,
            [styles.minimal]: isMinimal,
            [styles.componentSubcomponentsInspire]: variant === "inspire",
          })}
        >
          {component.subcomponents?.map((subComponent) => (
            <ComponentItem
              key={subComponent.id}
              component={subComponent}
              componentStatusLookup={componentStatusLookup}
              componentStatusRank={componentStatusRank}
              showResponseTime={showResponseTime}
              showComponentDescriptions={showComponentDescriptions}
              timezone={timezone}
              timeFormat={timeFormat}
              level={level + 1}
              componentLinkTo={componentLinkTo}
              layout={layout}
              variant={variant}
            />
          ))}
        </div>
      )}
    </div>
  );
};
